import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { OrdersBoxIcon } from "@features/orders-features/components/OrdersBoxIcon";
import ApartmentIcon from "@mui/icons-material/Apartment";
import { Box, Typography } from "@mui/material";
import shiPalette from "shared-ui/src/theme/shiPalette";
import { OrderRow } from "@features/orders-features/types/types";
import { OrderSyncIndicator } from "@features/orders-features/components/OrderSyncIndicator";
import type { OrderSyncStatus } from "@features/orders-features/hooks/useOrdersStream";
import {
  buildOrderProgressTracker,
  OrderProgressStepViewModel,
} from "@features/orders-features/functions/orderProgressTracker";

interface OrderDrawerOrderProgressProps {
  order: OrderRow;
  syncStatus?: OrderSyncStatus;
  lastSyncedAt?: Date | null;
  disableGutters?: boolean;
}

function getBaseStepIcon(stepKey: OrderProgressStepViewModel["key"]) {
  switch (stepKey) {
    case "orderPlaced":
      return OrdersBoxIcon;
    case "processing":
      return Inventory2OutlinedIcon;
    case "shipped":
      return LocalShippingOutlinedIcon;
    case "delivered":
      return ApartmentIcon;
    default:
      return CheckCircleIcon;
  }
}

function getBarColor(step: OrderProgressStepViewModel): string {
  if (step.active) {
    return shiPalette.info.light;
  }

  if (step.completed) {
    return shiPalette.success.light;
  }

  return shiPalette.table.border;
}

function getIconColor(step: OrderProgressStepViewModel): string {
  if (step.active) {
    return shiPalette.info.light;
  }

  if (step.completed) {
    return shiPalette.success.light;
  }

  return shiPalette.text.secondary;
}

export function OrderDrawerOrderProgress({
  order,
  syncStatus,
  lastSyncedAt,
  disableGutters = false,
}: Readonly<OrderDrawerOrderProgressProps>) {
  const tracker = buildOrderProgressTracker(order);

  return (
    <Box sx={{ px: disableGutters ? 0 : 2, pt: disableGutters ? 0 : 2, pb: 0 }}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          borderRadius: 1,
          p: 0,
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            component="h3"
            variant="subtitle1"
            sx={{ textTransform: "uppercase", fontWeight: "normal" }}
          >
            Order Progress
          </Typography>
          {syncStatus && syncStatus !== "idle" && (
            <OrderSyncIndicator
              variant="compact"
              syncStatus={syncStatus}
              lastSyncedAt={lastSyncedAt ?? null}
            />
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 1.5,
            p: 2,
          }}
        >
          {tracker.steps.map((step) => {
            const Icon = step.active ? getBaseStepIcon(step.key) : (step.completed ? CheckCircleIcon : getBaseStepIcon(step.key));

            return (
              <Box key={step.key}>
                <Icon
                  fontSize="medium"
                  sx={{
                    mb: 0.5,
                    color: getIconColor(step),
                  }}
                />
                <Typography variant="body2" sx={{ lineHeight: 1.3, mb: 0.75 }}>
                  {step.label}
                </Typography>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: 1,
                    bgcolor: getBarColor(step),
                  }}
                />
                {step.key === "delivered" && tracker.deliveredAuxText ? (
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1 }}
                    >
                      {step.dateText ?? "\u2014"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        color: "success.dark",
                        fontWeight: 600,
                      }}
                    >
                      {tracker.deliveredAuxText}
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 1 }}
                  >
                    {step.dateText ?? "\u2014"}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
